'use client'
import React, { FC, PropsWithChildren } from 'react'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import useWindowSize from '@/utils/hooks/useWindowSize'
import { Collection } from '@/utils/types/collections'
import ChartIcon from '@/icons/chart'

const FeedHeader: FC<
	PropsWithChildren<{
		className?: string
		gridView?: boolean
		hideViewSelector?: boolean
		setGridView?: (value: any) => void
		setChartView?: (value: any) => void
		chartView?: boolean
		title?: string
		mobileTitle?: boolean
		label?: string
		tabs?: { title: string; selected: boolean; onClick: () => void }[]
		selectedOption?: string
		options?: { title: string; id: string; onClick: () => void; collection?: Collection }[]
		secondaryOptions?: { title: string; id: string; onClick: () => void }[]
		selectedSecondaryOption?: string
		divider?: boolean
		search?: string
		setSearch?: (value: any) => void
		collectionFilters?: any[]
		collectionFilter?: string
		setCollectionFilter?: (filter: string, slug: string) => void
		selectedCollectionSlug?: string
	}>
> = (props) => {
	const {
		title,
		mobileTitle: _mobileTitle,
		tabs,
		selectedOption,
		options,
		hideViewSelector: _hideViewSelector,
		collectionFilters,
		collectionFilter,
		setCollectionFilter,
		chartView,
		setChartView,
		selectedSecondaryOption,
		secondaryOptions
	} = props
	const { width } = useWindowSize()

	const renderSelect = (options: any, value: any, onChange: any, label = 'Sort by') => (
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger aria-label={label} className="bg-background border-input w-auto min-w-[140px] h-11">
				<SelectValue />
			</SelectTrigger>
			<SelectContent className="max-h-[var(--radix-select-content-available-height)]">
				{options?.map((option: any) => (
					<SelectItem key={option.id} value={option.id} className="min-h-11">
						{option.title}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)

	return (
		<div
			className={cn(
				'flex flex-col gap-4 w-full mb-6 mt-6 px-4',
				{
					[(props.className as any)]: !!props.className,
					'pt-0': !title
				}
			)}
		>
			{/* Title and filters row - side by side on desktop, stacked on mobile */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				{/* Title/Tabs */}
				{(tabs as any)?.length > 0 ? (
					<div className="flex items-center gap-2">
						{(tabs as any).map((tab: any, index: any) => (
							<button
								key={index}
								onClick={tab.onClick}
								className={cn(
									'px-4 py-2 text-sm font-medium rounded-full',
									tab.selected
										? 'bg-primary text-primary-foreground'
										: 'text-muted-foreground hover:text-foreground'
								)}
							>
								{tab.title}
							</button>
						))}
					</div>
				) : title ? (
					<h2 className="text-xl font-semibold text-foreground">{title}</h2>
				) : null}
				
				{/* Filters - aligned right on desktop */}
				<div className={cn(
					'flex flex-wrap items-center gap-2 sm:gap-4',
					{ 'justify-start md:justify-end': true }
				)}>
					{typeof props.search !== 'undefined' && title !== 'Collect' && (
						<Input
							className="hidden md:block flex-1 max-w-md text-sm font-medium border-input rounded-full h-11"
							value={props.search}
							onChange={(e) => (props as any).setSearch(e.target.value)}
							placeholder="Search"
						/>
					)}
					{(options as any)?.length > 0 && ((width as any) <= 921 && (collectionFilters as any)?.length > 0 ? (
						<div className={cn(
							'w-full md:w-auto',
							(width as any) <= 921 && typeof setCollectionFilter === 'function' ? 'mb-4 md:mb-0' : ''
						)}>
							{renderSelect(options, selectedOption, (value: any) => {
									const option = (options as any).find((opt: any) => opt.id === value)
								option?.onClick()
							})}
						</div>
					) : (
						<div data-feed-filter-group className="flex items-center gap-2 sm:gap-4">
							{(secondaryOptions as any)?.length > 0 && renderSelect(
								secondaryOptions,
								selectedSecondaryOption,
								(value: any) => {
										const option = (secondaryOptions as any).find((opt: any) => opt.id === value)
									option?.onClick()
								},
								'Category'
							)}
							{renderSelect(options, selectedOption, (value: any) => {
									const option = (options as any).find((opt: any) => opt.id === value)
								option?.onClick()
							})}
						</div>
					))}
					
					{(options as any)?.length > 0 && (width as any) <= 921 && typeof setCollectionFilter === 'function' && (
						<div className="w-full md:w-auto">
							{renderSelect(
								collectionFilters,
								collectionFilter,
								(value: any) => {
										const option = (collectionFilters as any).find((opt: any) => opt.id === value)
									if (option && setCollectionFilter) {
										setCollectionFilter(option.id, option.slug || '')
									}
								}
							)}
						</div>
					)}
					{setChartView && (
						<ChartIcon
							onClick={() => setChartView(!chartView)}
							className={cn(
								'cursor-pointer size-6 [stroke:rgba(255,255,255,.3)] hover:[stroke:white]',
								{
									'[stroke:white]': chartView
								}
							)}
						/>
					)}
				</div>
			</div>
			{props.children}
		</div>
	)
}

export default FeedHeader
